import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment, MenuItem,
    Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { dateToString, stringToDate } from '../../../utils/utils';
import { formatCurrency } from '../../../utils/financialCalculations';
import { lifeCarePlanCategoryConfig as categoryConfig } from '../lifeCarePlanCategoryConfig';

const createNewItem = (category) => {
    const baseItem = { id: `item-${Date.now()}-${Math.random()}` };
    const columns = categoryConfig[category]?.columns || [];
    columns.forEach(col => {
        baseItem[col.field] = '';
    });
    return baseItem;
};

const LifeCarePlanDetailsStep = ({ formik }) => {
    const { values, setFieldValue, errors } = formik;

    // Initialize lifeCarePlan in formik if it doesn't exist
    useEffect(() => {
        if (!values.lifeCarePlan) {
            setFieldValue('lifeCarePlan', {
                therapies: [],
                medicalCare: [],
                nursingCare: [],
                pharmacy: [],
            });
        }
    }, [values.lifeCarePlan, setFieldValue]);

    const [modalOpen, setModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentCategory, setCurrentCategory] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [modalErrors, setModalErrors] = useState({});

    const lifeCarePlan = values.lifeCarePlan || { therapies: [], medicalCare: [], nursingCare: [], pharmacy: [] };

    const handleOpenAddModal = (category) => {
        setCurrentItem(createNewItem(category));
        setCurrentCategory(category);
        setEditingIndex(null);
        setModalErrors({});
        setModalOpen(true);
    };

    const handleOpenEditModal = (item, index, category) => {
        setCurrentItem({ ...item });
        setCurrentCategory(category);
        setEditingIndex(index);
        setModalErrors({});
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentItem(null);
        setCurrentCategory('');
        setEditingIndex(null);
    };

    const handleModalChange = (event) => {
        const { name, value } = event.target;
        setCurrentItem(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!modalOpen) {
            setModalErrors({});
            return;
        }

        const newErrors = {};
        if (currentCategory && currentItem) {
            const columnsForCategory = categoryConfig[currentCategory]?.columns || [];
            for (const col of columnsForCategory) {
                if (col.required && !col.readOnly) {
                    const value = currentItem[col.field];
                    if (value === null || value === undefined || value === '') {
                        newErrors[col.field] = `${col.headerName} is required.`;
                    }
                }
            }
        }
        setModalErrors(newErrors);
    }, [currentItem, currentCategory, modalOpen]);

    useEffect(() => {
        if (currentItem && currentCategory) {
            const config = categoryConfig[currentCategory];
            if (!config) return;

            const {
                quantity,
                frequencyPerYear,
                costPerTreatment,
                frequency,
                costPerUnit,
                frequencyPerWeek,
            } = currentItem;

            let newAnnualCost = 0;

            if (currentCategory === 'therapies' || currentCategory === 'medicalCare') {
                const q = parseFloat(quantity) || 0;
                const fpy = parseFloat(frequencyPerYear) || 0;
                const cpt = parseFloat(costPerTreatment) || 0;
                newAnnualCost = q * fpy * cpt;
            } else if (currentCategory === 'nursingCare') {
                const q = parseFloat(quantity) || 0;
                const cpu = parseFloat(costPerUnit) || 0;
                let multiplier = 0;
                switch (frequency) {
                    case 'Per Day':
                        multiplier = 365;
                        break;
                    case 'Per Week':
                        multiplier = 52;
                        break;
                    case 'Per Month':
                        multiplier = 12;
                        break;
                    case 'Per Year':
                        multiplier = 1;
                        break;
                    default:
                        multiplier = 0;
                }
                newAnnualCost = q * cpu * multiplier;
            } else if (currentCategory === 'pharmacy') {
                const q = parseFloat(quantity) || 0;
                const fpw = parseFloat(frequencyPerWeek) || 0;
                const cpu = parseFloat(costPerUnit) || 0;
                newAnnualCost = q * fpw * cpu * 52;
            }

            if (currentItem.annualCost !== newAnnualCost) {
                setCurrentItem(prev => ({ ...prev, annualCost: newAnnualCost }));
            }
        }
    }, [currentItem, currentCategory]);

    const handleSave = () => {
        const categoryItems = [...(lifeCarePlan[currentCategory] || [])];
        let updatedItems;

        if (editingIndex !== null) {
            updatedItems = [...categoryItems];
            updatedItems[editingIndex] = currentItem;
        } else {
            updatedItems = [...categoryItems, currentItem];
        }

        setFieldValue(`lifeCarePlan.${currentCategory}`, updatedItems);
        handleCloseModal();
    };

    const removeItem = (category, index) => {
        const updatedItems = (lifeCarePlan[category] || []).filter((_, i) => i !== index);
        setFieldValue(`lifeCarePlan.${category}`, updatedItems);
    };


    const renderTable = (category, title, columns) => (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map(col => <TableCell key={col.field} align={col.align ?? 'left'}>{col.headerName}</TableCell>)}
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(lifeCarePlan[category] || []).map((item, index) => (
                            <TableRow key={item.id || index}>
                                {columns.map(col => (
                                    <TableCell key={col.field} align={col.align ?? 'left'}>
                                        {col.isCurrency ? formatCurrency(item[col.field]) : (item[col.field] || '-')}
                                    </TableCell>
                                ))}
                                <TableCell align="center">
                                    <IconButton onClick={() => handleOpenEditModal(item, index, category)} color="primary" size="small">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => removeItem(category, index)} color="error" size="small">
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleOpenAddModal(category)}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
            >
                Add {title} Item
            </Button>
        </Box>
    );

    const modalColumns = categoryConfig[currentCategory]?.columns || [];

    return (
        <>
            <Typography variant="h5" gutterBottom>
                Life Care Plan Details
            </Typography>
            {errors?.lifeCarePlan && typeof errors.lifeCarePlan === 'string' && (
                <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>
                    {errors.lifeCarePlan}
                </Typography>
            )}

            {Object.entries(categoryConfig).map(([key, config]) =>
                renderTable(key, config.title, config.columns)
            )}

            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingIndex !== null ? `Edit ${categoryConfig[currentCategory]?.title || 'Item'}` : `Add New ${categoryConfig[currentCategory]?.title || 'Item'}`}
                </DialogTitle>
                <DialogContent>
                    {currentItem && (
                        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                {modalColumns.map(col => (
                                    <Grid size={{xs:12, sm:6, md:4}} key={col.field}>
                                        {col.type === 'date' ? (
                                            <DatePicker
                                                label={col.headerName}
                                                value={stringToDate(currentItem[col.field])}
                                                onChange={(newValue) => setCurrentItem(prev => ({ ...prev, [col.field]: dateToString(newValue) }))}
                                                format="yyyy-MM-dd"
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: 'outlined',
                                                        required: col.required,
                                                        margin: "dense",
                                                        id: col.field,
                                                        name: col.field,
                                                        error: !!modalErrors[col.field],
                                                        helperText: modalErrors[col.field] || `${col.helperText} (YYYY-MM-DD)`,
                                                    }
                                                }}
                                            />
                                        ) : col.select ? (
                                            <TextField
                                                required={col.required}
                                                margin="dense"
                                                id={col.field}
                                                name={col.field}
                                                label={col.headerName}
                                                value={currentItem[col.field] || ''}
                                                onChange={handleModalChange}
                                                fullWidth
                                                variant="outlined"
                                                select
                                                error={!!modalErrors[col.field]}
                                                helperText={modalErrors[col.field] || col.helperText}
                                            >
                                                {col.options?.map(option => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : col.readOnly  && col.adornment === '$'
                                        ? 
                                        (
                                            <TextField
                                                required={col.required}
                                                margin="dense"
                                                id={col.field}
                                                name={col.field}
                                                label={col.headerName}
                                                type={col.type || 'text'}
                                                value={formatCurrency(currentItem[col.field] ?? '')}
                                                onChange={handleModalChange}
                                                fullWidth
                                                variant="outlined"
                                                disabled={col.readOnly}
                                                error={!!modalErrors[col.field]}
                                                helperText={modalErrors[col.field] || col.helperText}
                                            />
                                        ):
                                        (
                                            <TextField
                                                required={col.required}
                                                margin="dense"
                                                id={col.field}
                                                name={col.field}
                                                label={col.headerName}
                                                type={col.type || 'text'}
                                                value={currentItem[col.field] || ''}
                                                onChange={handleModalChange}
                                                fullWidth
                                                variant="outlined"
                                                disabled={col.readOnly}
                                                slotProps= {{
                                                    input: col.adornment ? {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                {col.adornment}
                                                            </InputAdornment>
                                                        )
                                                    } : {}
                                                }}
                                                error={!!modalErrors[col.field]}
                                                helperText={modalErrors[col.field] || col.helperText}
                                            />
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={Object.keys(modalErrors).length > 0}>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LifeCarePlanDetailsStep;